import { useAnalytics } from '@/contexts/AnalyticsContext';
import { AgentRunAttachment } from '@superfunnel/types';
import { useEffect, useRef } from 'react';
import {
  AgentChatProvider,
  useAgentChat,
} from '../../contexts/AgentChatContext';
import { EditorProvider } from './EditorContext';
import { EditorHeader } from './EditorHeader';
import { EditorMainContent } from './EditorMainContent';

interface GlobalSectionEditorProps {
  mode: 'globalSection';
  globalSectionId: string;
  onClose: () => void;
  initialMessage?:
    | string
    | { content: string; attachments?: AgentRunAttachment[] };
  initialShowAnalytics?: boolean;
}

interface WebsiteEditorProps {
  mode: 'website';
  dynamicPageId: string;
  initialVariantId: string;
  initialWebsiteId: string;
  onClose: () => void;
  initialMessage?:
    | string
    | { content: string; attachments?: AgentRunAttachment[] };
  initialShowAnalytics?: boolean;
}

type EditorProps = GlobalSectionEditorProps | WebsiteEditorProps;

// Internal wrapper that handles initial message
function EditorWithInitialMessage({
  initialMessage,
  initialShowAnalytics,
  ...props
}: EditorProps) {
  const { track } = useAnalytics();
  const { sendMessage, getAgentState } = useAgentChat();
  const agentState = getAgentState();
  const initialMessageSentRef = useRef(false);
  const editorOpenedTrackedRef = useRef(false);

  // Track editor opening (only once)
  useEffect(() => {
    if (!editorOpenedTrackedRef.current) {
      editorOpenedTrackedRef.current = true;
      track('editor_opened', {
        mode: props.mode,
        has_initial_message: !!initialMessage,
        dynamic_page_id:
          props.mode === 'website' ? props.dynamicPageId : undefined,
        global_section_id:
          props.mode === 'globalSection' ? props.globalSectionId : undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Send initial message when editor opens (only once)
  useEffect(() => {
    if (
      initialMessage &&
      agentState.messages.length === 0 &&
      !initialMessageSentRef.current
    ) {
      console.log('[Editor] Sending initial message:', initialMessage);

      const to = setTimeout(() => {
        initialMessageSentRef.current = true;
        const message =
          typeof initialMessage === 'string'
            ? initialMessage
            : initialMessage.content;

        track('editor_initial_message_sent', {
          mode: props.mode,
          message_length: message.length,
          has_attachments:
            typeof initialMessage !== 'string' &&
            !!initialMessage.attachments?.length,
          attachments_count:
            typeof initialMessage !== 'string'
              ? initialMessage.attachments?.length || 0
              : 0,
        });

        sendMessage(
          message,
          typeof initialMessage === 'string'
            ? undefined
            : initialMessage.attachments,
        );
      }, 300); // slight delay to ensure context is set up
      return () => clearTimeout(to);
    }
  }, [
    initialMessage,
    sendMessage,
    agentState.messages.length,
    track,
    props.mode,
  ]);

  return (
    <EditorProvider {...props} initialShowAnalytics={initialShowAnalytics}>
      <div className="absolute inset-0 bg-gray-50">
        {/* Header */}
        <EditorHeader />

        {/* Main Content */}
        <EditorMainContent />
      </div>
    </EditorProvider>
  );
}

// Main Editor component with single, comprehensive context
export default function Editor(props: EditorProps) {
  return (
    <AgentChatProvider agentName="landingPageAgent">
      <EditorWithInitialMessage {...props} />
    </AgentChatProvider>
  );
}
